import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { motion } from "framer-motion";
import { Sparkles, TrendingUp, TrendingDown, Clock, ArrowUpRight, ChevronDown, Gauge, RefreshCw } from "lucide-react";
import ReliabilityBadge from "@/components/ReliabilityBadge";

const TYPE = {
  raise_price: { color: "var(--demand-low)", icon: TrendingUp },
  reduce_price: { color: "var(--demand-high)", icon: TrendingDown },
  behind_pace: { color: "var(--demand-high)", icon: TrendingDown },
  last_minute: { color: "var(--demand-medium)", icon: Clock },
};

function Conf({ v }) {
  const c = v >= 0.7 ? "var(--demand-low)" : v >= 0.5 ? "var(--demand-medium)" : "var(--demand-high)";
  return <span className="text-[11px] font-bold font-data px-1.5 py-0.5 rounded" style={{ backgroundColor: `color-mix(in srgb, ${c} 18%, transparent)`, color: c }}>{Math.round(v * 100)}%</span>;
}

export default function DecisionCenter({ data, loading, today, recDayIsToday = true, recDayLabel, onRefresh }) {
  const { t, lang } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const [open, setOpen] = useState(null);
  const eur = (n) => new Intl.NumberFormat(lang === "it" ? "it-IT" : "en-US").format(n || 0);
  const fmtDate = (d) => new Date(d).toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { weekday: "short", day: "numeric", month: "short" });

  // La fonte reale del "prezzo attuale": solo le letture Booking sono davvero "lette su
  // Booking"; manuale/applicato = impostato dall'utente; imported = da file PMS.
  const readSrc = today?.current_price_source || "";
  const isRealRead = readSrc.startsWith("booking");
  const readLabelBase = isRealRead
    ? L("Prezzo letto su Booking", "Read on Booking")
    : readSrc === "imported"
      ? L("Prezzo importato (PMS)", "Imported price (PMS)")
      : (readSrc === "manual" || readSrc === "applied")
        ? L("Prezzo impostato da te", "Your set price")
        : L("Prezzo attuale (stima)", "Current price (estimate)");

  return (
    <motion.div data-testid="decision-center" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="md:col-span-4 rounded-xl p-6 md:p-8 relative overflow-hidden border border-primary/25"
      style={{ background: "linear-gradient(135deg, hsl(var(--primary)/0.10), hsl(var(--card)))" }}>
      <div className="grain absolute inset-0 opacity-40" />
      <div className="relative">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-primary" />
              <h2 className="text-2xl font-bold">{t("decisionCenter")}</h2>
            </div>
            <p className="text-sm text-muted-foreground">{t("briefingSub")}</p>
            {data?.reliability && (
              <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="dc-reliability-global">
                <ReliabilityBadge rel={data.reliability} lang={lang} />
                {onRefresh && (
                  <button
                    data-testid="dc-refresh-btn"
                    onClick={onRefresh}
                    disabled={data?.freshness?.refreshing}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border border-border hover:bg-accent/50 transition-colors disabled:opacity-50"
                    title={L("Forza un aggiornamento dei dati (competitor e tuo prezzo) per le date mostrate", "Force a data refresh (competitors and your price) for the shown dates")}
                  >
                    <RefreshCw className={`w-3 h-3 ${data?.freshness?.refreshing ? "animate-spin" : ""}`} />
                    {L("Aggiorna dati", "Refresh data")}
                  </button>
                )}
                {data?.freshness?.refreshing && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="dc-refreshing">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    {L("Aggiornamento dati in corso…", "Refreshing data…")}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{t("todaysUplift")}</p>
            <p data-testid="dc-uplift" className="font-data text-3xl font-bold text-primary">+€{eur(data?.total_estimated_uplift)}</p>
            <p className="text-[11px] text-muted-foreground">{t("ifFollowed")}</p>
          </div>
        </div>

        {today && (today.recommended_price != null || today.current_price != null) && (
          <div data-testid="dc-today" className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <div className="rounded-lg border-2 border-primary/30 bg-primary/5 px-4 py-3">
              <p className="text-[11px] uppercase tracking-widest font-bold text-primary">{(recDayIsToday ? L("Prezzo consigliato oggi", "Recommended today") : L(`Prezzo consigliato ${recDayLabel}`, `Recommended ${recDayLabel}`)) + (today.city_tax_night > 0 ? L(" (senza tassa)", " (tax excl.)") : "")}</p>
              {!recDayIsToday && <p className="text-[10px] text-[var(--demand-high)] font-semibold -mt-0.5 mb-0.5">{L("Oggi tutto occupato → primo giorno disponibile", "Today sold out → first available day")}</p>}
              <p className="font-data text-3xl font-bold text-primary" data-testid="dc-today-rec">€{today.recommended_price ?? "—"}</p>
              {today.city_tax_night > 0 && today.recommended_price_gross != null && (
                <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="dc-today-rec-gross">{L("con tassa", "with tax")} <b className="text-foreground">€{today.recommended_price_gross}</b> · {L("da pubblicare su Booking", "to publish on Booking")}</p>
              )}
              {today.premium_floor?.applied && (
                <p className="text-[11px] text-emerald-600 font-semibold mt-1" data-testid="dc-premium-floor">
                  {(() => {
                    const mm = today.premium_floor.period_month;
                    const names = lang === "it"
                      ? ["", "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"]
                      : ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
                    const per = mm ? (lang === "it" ? ` di ${names[parseInt(mm, 10)]}` : ` for ${names[parseInt(mm, 10)]}`) : "";
                    return L(
                      `Resta solo camera premium: non scendo sotto il minimo storico${per} (€${today.premium_floor.floor_net} netto di tasse e sconti).`,
                      `Only a premium room left: not going below the historical minimum${per} (€${today.premium_floor.floor_net} net of tax & discounts).`);
                  })()}
                </p>
              )}
            </div>
            <div className="rounded-lg border border-border bg-background/60 px-4 py-3">
              {today.booking_public ? (
                <>
                  <p className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{L("Prezzo che vede il cliente su Booking", "Price guests see on Booking") + (!recDayIsToday ? ` · ${recDayLabel}` : "")}</p>
                  <p className="font-data text-3xl font-bold" data-testid="dc-today-public">€{today.booking_public.price}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="dc-today-full-price">
                    {L("prezzo pieno", "full price")} <s className="text-foreground">€{today.booking_public.full_price}</s>
                    {today.booking_public.offer_pct ? ` · ${L("offerte attive", "active offers")} −${today.booking_public.offer_pct}%` : ""}
                    {` · ${L("calcoli netti col tuo sconto manuale", "net maths uses your manual discount")}`}
                  </p>
                </>
              ) : (
                <>
              <p className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{(readLabelBase + (today.city_tax_night > 0 ? L(" (senza tassa)", " (tax excl.)") : "")) + (!recDayIsToday ? ` · ${recDayLabel}` : "")}</p>
              <p className="font-data text-3xl font-bold" data-testid="dc-today-read">€{(today.city_tax_night > 0 ? today.current_price_net_of_tax : today.current_price) ?? "—"}</p>
              {today.city_tax_night > 0 && today.current_price != null && (
                <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="dc-today-read-gross">{L("lordo (tassa incl.)", "gross (tax incl.)")} <b className="text-foreground">€{today.current_price}</b> · {L("tassa", "tax")} €{today.city_tax_night}</p>
              )}
                </>
              )}
            </div>
            {today.price_read_check?.mismatch && (
              <div className="sm:col-span-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-4 py-2 text-xs" data-testid="dc-read-mismatch">
                <span className="font-semibold text-amber-500">⚠️ </span>
                {L(
                  `Il prezzo letto su Booking (€${today.price_read_check.read}) è diverso dal prezzo ${today.price_read_check.applied_source === "manual" ? "impostato" : "applicato"} (€${today.price_read_check.applied}) — scarto ${today.price_read_check.diff_pct}%.`,
                  `The price read on Booking (€${today.price_read_check.read}) differs from your ${today.price_read_check.applied_source === "manual" ? "set" : "applied"} price (€${today.price_read_check.applied}) — ${today.price_read_check.diff_pct}% gap.`
                )}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground py-6">{lang === "it" ? "L'AI sta preparando il tuo briefing..." : "AI is preparing your briefing..."}</p>
        ) : !data || data.top_actions.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6">{t("noActions")}</p>
        ) : (
          <>
            {data.summary && <p className="text-sm mb-4 bg-background/50 rounded-lg p-3 border border-border">{data.summary}</p>}
            <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3">{t("topActions")}</p>
            <div className="space-y-2 mb-6" data-testid="dc-actions">
              {data.top_actions.map((a, i) => {
                const ty = TYPE[a.type] || TYPE.raise_price;
                const Icon = ty.icon;
                const isOpen = open === a.id;
                return (
                  <div key={a.id} data-testid={`dc-action-${i}`} className="bg-background/60 border border-border rounded-lg overflow-hidden">
                    <button onClick={() => setOpen(isOpen ? null : a.id)} className="w-full flex items-center gap-3 p-3 text-left hover:bg-accent/40 transition-colors">
                      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `color-mix(in srgb, ${ty.color} 18%, transparent)` }}>
                        <Icon className="w-4 h-4" style={{ color: ty.color }} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm">{a.title}</span>
                          <span className="text-xs text-muted-foreground font-data">{fmtDate(a.date)}</span>
                        </span>
                        {(a.read_price != null || a.current_price != null) && a.suggested_price != null && (
                          <span className="flex flex-col text-xs font-data">
                            <span className="text-primary font-semibold">
                              {L("Prezzo attuale su Booking", "Current price on Booking")}: €{a.read_price != null ? a.read_price : a.current_price} → {L("consigliato", "suggested")} €{a.suggested_price_gross != null ? a.suggested_price_gross : a.suggested_price}
                            </span>
                            {data?.city_tax_night > 0 && (
                              <span className="text-muted-foreground text-[10px]">{L("senza tassa", "tax excl.")}: €{a.read_price_net_of_tax != null ? a.read_price_net_of_tax : a.current_price} → €{a.suggested_price}</span>
                            )}
                          </span>
                        )}
                      </span>
                      <span className="text-right shrink-0">
                        <span className="font-data font-bold text-primary flex items-center gap-0.5"><ArrowUpRight className="w-3.5 h-3.5" />€{eur(a.estimated_additional_revenue)}</span>
                        <Conf v={a.confidence} />
                        {a.reliability && (
                          <span className="block mt-0.5 text-[10px] font-bold font-data" data-testid={`dc-action-rel-${i}`}
                            style={{ color: a.reliability.color === "green" ? "#16a34a" : a.reliability.color === "red" ? "#dc2626" : "#d97706" }}>
                            {L("Affid.", "Rel.")} {a.reliability.score}%
                          </span>
                        )}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isOpen && (
                      <div className="px-3 pb-3 pt-1 border-t border-border/50">
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><Gauge className="w-3 h-3" />{t("whyGenerated")}</p>
                        <p className="text-sm text-muted-foreground">{a.explanation}</p>
                        {a.reliability && (
                          <div className="mt-2" data-testid={`dc-action-reliability-${i}`}>
                            <ReliabilityBadge rel={a.reliability} lang={lang} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div data-testid="dc-behind-pace">
                <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2 flex items-center gap-1.5"><TrendingDown className="w-3.5 h-3.5" style={{ color: "var(--demand-high)" }} />{t("behindPaceDates")}</p>
                {data.behind_pace.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {data.behind_pace.map((r) => (
                      <span key={r.date} className="text-xs bg-background/60 border border-border rounded px-2 py-1 font-data">{r.date.slice(5)} · {r.pace_ratio}×</span>
                    ))}
                  </div>
                ) : <p className="text-xs text-muted-foreground">—</p>}
              </div>
              <div data-testid="dc-price-increase">
                <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" style={{ color: "var(--demand-low)" }} />{t("priceIncreaseDates")}</p>
                {data.price_increase_dates.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {data.price_increase_dates.map((r) => (
                      <span key={r.date} className="text-xs bg-background/60 border border-border rounded px-2 py-1 font-data">{r.date.slice(5)} · +{r.pct}%</span>
                    ))}
                  </div>
                ) : <p className="text-xs text-muted-foreground">—</p>}
              </div>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
